import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Lock, Sparkles, LogOut, User, LogIn, ShieldAlert, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { RupiahInput } from "@/components/RupiahInput";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import { BackupRestore } from "@/components/BackupRestore";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { getPricing, formatPrice } from "@/lib/pricing";

export default function Profile() {
  const navigate = useNavigate();
  const { report, updateData, premium, simulationActive, resetAll } = useApp();
  const { user, isAuthenticated, isAdmin, logout, deleteAccount } = useAuth();
  const pricing = getPricing();
  const [data, setData] = useState({ ...(report?.data || {}), sideIncomes: [...((report?.data && report.data.sideIncomes) || [])] });

  const handleLogout = async () => {
    await logout();
    toast.success("Anda telah keluar");
    navigate("/");
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      toast.success("Akun & laporan tersimpan di server telah dihapus");
    } catch (e) {
      toast.error(e.message || "Gagal menghapus akun");
    }
  };

  const editable = simulationActive || !premium;
  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  const addSide = () =>
    set({ sideIncomes: [...data.sideIncomes, { id: `s_${Date.now()}`, name: "", amount: 0 }] });
  const updateSide = (id, patch) =>
    set({ sideIncomes: data.sideIncomes.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  const removeSide = (id) =>
    set({ sideIncomes: data.sideIncomes.filter((s) => s.id !== id) });

  const save = () => {
    updateData(data);
    toast.success("Laporan diperbarui");
    navigate("/app/ketahanan");
  };

  return (
    <div className="px-6 pb-28 pt-8">
      <h1 className="mb-1 font-heading text-2xl font-extrabold text-zinc-900">Profil</h1>
      <p className="mb-5 text-sm text-zinc-400">Data keluarga &amp; kondisi keuangan</p>

      {/* Financial setup & navigation */}
      <div className="mb-4 overflow-hidden rounded-3xl border border-zinc-200 bg-white shield-shadow" data-testid="profil-finance-links">
        {[
          { to: "/app/keuangan", label: "Pengaturan Keuangan", sub: "Penghasilan, pengeluaran rutin, cicilan, dana darurat" },
          { to: "/app/catatan", label: "Catatan Transaksi", sub: "Lihat & kelola transaksi harian" },
          { to: "/app/anggaran", label: "Rencana Anggaran", sub: "Alokasi anggaran keluarga" },
        ].map((l) => (
          <button
            key={l.to}
            data-testid={`profil-link-${l.to.split("/").pop()}`}
            onClick={() => navigate(l.to)}
            className="flex w-full items-center justify-between border-b border-zinc-100 px-5 py-3.5 text-left last:border-0"
          >
            <div>
              <p className="text-sm font-semibold text-zinc-800">{l.label}</p>
              <p className="text-xs text-zinc-400">{l.sub}</p>
            </div>
            <span className="text-zinc-300">›</span>
          </button>
        ))}
      </div>

      {/* Account */}
      {isAuthenticated ? (
        <div className="mb-4 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid="account-card">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-50">
              <User className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-heading text-sm font-bold text-zinc-900" data-testid="account-email">
                {user.email}
              </p>
              <p className="text-xs text-zinc-400">Laporan tersimpan di akun Anda</p>
            </div>
          </div>

          {isAdmin && (
            <Button
              data-testid="account-admin-link"
              onClick={() => navigate("/admin")}
              variant="outline"
              className="mt-4 h-10 w-full rounded-full border-zinc-300 text-sm font-semibold"
            >
              <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard Admin
            </Button>
          )}

          <button
            data-testid="account-logout-btn"
            onClick={handleLogout}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-zinc-200 py-2.5 text-sm font-semibold text-zinc-700"
          >
            <LogOut className="h-4 w-4" /> Keluar
          </button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="account-delete-btn"
                className="mt-3 flex w-full items-center justify-center gap-2 py-1.5 text-xs font-semibold text-rose-500"
              >
                <ShieldAlert className="h-3.5 w-3.5" /> Hapus akun &amp; semua data di server
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-account-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Hapus akun?</AlertDialogTitle>
                <AlertDialogDescription>
                  Tindakan ini permanen. Akun dan semua laporan yang tersimpan di server akan dihapus. Data di
                  perangkat ini tidak terpengaruh.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-account-cancel">Batal</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="delete-account-confirm"
                  onClick={handleDeleteAccount}
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  Ya, hapus akun
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : (
        <div className="mb-4 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid="account-guest-card">
          <p className="font-heading text-sm font-bold text-zinc-900">Simpan laporan Anda</p>
          <p className="mt-1 text-xs text-zinc-500">
            Buat akun untuk membuka laporan dari perangkat lain. Angka keuangan Anda tetap di perangkat ini dan tidak dikirim ke server kami.
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              data-testid="account-register-btn"
              onClick={() => navigate("/register?next=/app/profil")}
              className="h-10 flex-1 rounded-full bg-zinc-900 text-sm font-semibold hover:bg-zinc-800"
            >
              Daftar
            </Button>
            <Button
              data-testid="account-login-btn"
              onClick={() => navigate("/login?next=/app/profil")}
              variant="outline"
              className="h-10 flex-1 rounded-full border-zinc-300 text-sm font-semibold"
            >
              <LogIn className="mr-1.5 h-4 w-4" /> Masuk
            </Button>
          </div>
        </div>
      )}

      <SubscriptionCard />

      {isAuthenticated && <BackupRestore />}


      {premium && !simulationActive && (
        <div
          className="mb-4 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow"
          data-testid="profile-locked-banner"
        >
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-zinc-500" />
            <p className="font-heading text-sm font-bold text-zinc-900">Pengeditan terkunci</p>
          </div>
          <p className="mt-2 text-xs text-zinc-500">
            Periode simulasi laporan ini telah berakhir. Buat laporan baru untuk memperbarui data.
          </p>
          <Button
            data-testid="profile-new-report-cta"
            onClick={() => navigate("/paywall")}
            className="mt-3 h-11 w-full rounded-full bg-zinc-900 text-sm font-semibold hover:bg-zinc-800"
          >
            Update Kondisi &amp; Buat Laporan Baru — {formatPrice(pricing.price)}
          </Button>
        </div>
      )}

      <fieldset disabled={!editable || !report} className={`${!report ? "hidden" : ""} ${editable ? "" : "pointer-events-none opacity-60"}`}>
        <div className="space-y-4 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-zinc-700 font-heading">Nama keluarga</Label>
            <Input
              data-testid="profile-family-name"
              value={data.familyName}
              onChange={(e) => set({ familyName: e.target.value })}
              className="h-11 rounded-xl border-zinc-200"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-zinc-700 font-heading">Nama Suami</Label>
              <Input value={data.husbandName} onChange={(e) => set({ husbandName: e.target.value })} className="h-11 rounded-xl border-zinc-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-zinc-700 font-heading">Nama Istri</Label>
              <Input value={data.wifeName} onChange={(e) => set({ wifeName: e.target.value })} className="h-11 rounded-xl border-zinc-200" />
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-4 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 font-heading">Pendapatan</p>
          <RupiahInput label="Gaji Utama Suami" testId="profile-husband-salary" value={data.husbandSalary} onChange={(v) => set({ husbandSalary: v })} />
          <RupiahInput label="Gaji Utama Istri" testId="profile-wife-salary" value={data.wifeSalary} onChange={(v) => set({ wifeSalary: v })} />

          <div className="space-y-3 border-t border-zinc-100 pt-3">
            <p className="text-xs font-semibold text-zinc-500">Penghasilan Tambahan</p>
            {data.sideIncomes.map((s) => (
              <div key={s.id} className="rounded-2xl border border-zinc-100 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <Input
                    data-testid={`profile-side-name-${s.id}`}
                    value={s.name}
                    onChange={(e) => updateSide(s.id, { name: e.target.value })}
                    placeholder="Nama sumber"
                    className="mr-2 h-10 rounded-xl border-zinc-200"
                  />
                  <button data-testid={`profile-side-remove-${s.id}`} onClick={() => removeSide(s.id)} className="text-zinc-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <RupiahInput testId={`profile-side-amount-${s.id}`} value={s.amount} onChange={(v) => updateSide(s.id, { amount: v })} />
              </div>
            ))}
            <button
              data-testid="profile-add-side"
              onClick={addSide}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-zinc-200 py-2.5 text-sm font-semibold text-zinc-700"
            >
              <Plus className="h-4 w-4" /> Tambah
            </button>
          </div>
        </div>

        <div className="mt-4 space-y-4 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 font-heading">Kewajiban &amp; Pengeluaran</p>
          <RupiahInput label="KPR / Cicilan Rumah" testId="profile-mortgage" value={data.mortgage} onChange={(v) => set({ mortgage: v })} />
          <RupiahInput label="Cicilan Kendaraan" testId="profile-vehicle" value={data.vehicle} onChange={(v) => set({ vehicle: v })} />
          <RupiahInput label="Kartu Kredit" testId="profile-credit-card" value={data.creditCard} onChange={(v) => set({ creditCard: v })} />
          <RupiahInput label="Cicilan Lain" testId="profile-other-debt" value={data.otherDebt} onChange={(v) => set({ otherDebt: v })} />
          <RupiahInput label="Pengeluaran Rutin / bulan" testId="profile-living" value={data.livingExpenses} onChange={(v) => set({ livingExpenses: v })} />
          <RupiahInput label="Dana Darurat" testId="profile-emergency" value={data.emergencyFund} onChange={(v) => set({ emergencyFund: v })} />
        </div>
      </fieldset>

      {/* Coming soon */}
      <div className="mt-4 rounded-3xl border border-dashed border-zinc-200 p-5">
        <div className="flex items-center gap-2 text-zinc-400">
          <Sparkles className="h-4 w-4" />
          <p className="text-sm font-medium">Aset Keluarga — Segera hadir</p>
        </div>
      </div>

      {report && editable && (
        <Button
          data-testid="profile-save-btn"
          onClick={save}
          className="mt-5 h-14 w-full rounded-full bg-zinc-900 text-base font-semibold hover:bg-zinc-800"
        >
          Simpan &amp; Perbarui Laporan
        </Button>
      )}

      <button
        data-testid="profile-reset-btn"
        onClick={() => {
          resetAll();
          navigate("/");
        }}
        className="mt-4 w-full py-2 text-center text-xs text-zinc-400"
      >
        Mulai penilaian baru dari awal (hapus data)
      </button>
    </div>
  );
}
